import { useEffect, useState, memo } from 'react';
import {
    Button, Group, Stepper, Paper,
    Box
} from '@mantine/core';
import {
    IconRobot, IconRocket, IconTerminal, IconSettings,
    IconPlayerPlay
} from '@tabler/icons-react';
import { useBotSettings, useBotActions } from './BotContext';
import { AnimatePresence } from 'framer-motion';
import { NAME_DATA } from "../data/textTools";
import { COLORS } from '../config/constants';

import SetupStep from './setup/SetupStep';
import IdentityStep from './setup/IdentityStep';
import LaunchStep from './setup/LaunchStep';

const stepVariants = {
    hidden: { opacity: 0, x: 20 },
    visible: { opacity: 1, x: 0, transition: { duration: 0.3 } },
    exit: { opacity: 0, x: -20, transition: { duration: 0.2 } }
};

/**
 * BotSetupWizard component provides a multi-step form to configure and launch bots.
 * It manages the transition between different setup steps.
 */
const BotSetupWizard = memo(() => {
    const {
        form, setupStep, errors, loading
    } = useBotSettings();
    const {
        setForm, setSetupStep, setErrors, launchBots, connectSocket
    } = useBotActions();

    const [genMode, setGenMode] = useState('funny');

    useEffect(() => {
        connectSocket();
    }, []);

    /**
     * Generates a random bot name based on the selected generation mode.
     * @param {string} [modeOverride] - Optional mode to override the current generation mode.
     */
    const generateBotName = (modeOverride) => {
        const modeToUse = modeOverride || genMode;
        if (modeOverride) setGenMode(modeOverride);
        
        const list = (NAME_DATA && NAME_DATA[modeToUse]) ? NAME_DATA[modeToUse] : ["Bot", "Player", "Guest"];
        const name = list[Math.floor(Math.random() * list.length)];

        setForm(prev => ({ ...prev, name: name }));
        setErrors(prev => ({ ...prev, name: null }));
    };

    /**
     * Handles the transition to the next step in the setup wizard, validating fields if necessary.
     */
    const handleNext = () => {
        let isValid = true;
        if (setupStep === 0 && !form.pin.trim()) {
            setErrors(prev => ({ ...prev, pin: "Game PIN is required" })); isValid = false;
        }
        if (setupStep === 1 && !form.name.trim()) {
            setErrors(prev => ({ ...prev, name: "Bot Name is required" })); isValid = false;
        }
        if (isValid) setSetupStep((c) => (c < 2 ? c + 1 : c));
    };

    /**
     * Handles key down events for form inputs, enabling 'Enter' to progress or launch.
     * @param {React.KeyboardEvent} e - The keyboard event.
     * @param {string} target - The target action ('launch' or 'next').
     */
    const handleKeyDown = (e, target) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            target === 'launch' ? launchBots() : handleNext();
        }
    };

    return (
        <Box>
            <Paper radius="md" mb="md">
                <Stepper active={setupStep} size="sm" p="sm" allowNextStepsSelect={false} color={COLORS.BLUE}>
                    <Stepper.Step icon={<IconSettings size={18} />} label="Setup" />
                    <Stepper.Step icon={<IconRobot size={18} />} label="Identity" />
                    <Stepper.Step icon={<IconRocket size={18} />} label="Launch" />
                    <Stepper.Step icon={<IconTerminal size={18} />} label="Live" />
                </Stepper>
            </Paper>

            <Paper
                radius="md"
                p="lg"
                bg="transparent"
                style={{
                    height: 420,
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'space-between',
                    position: 'relative'
                }}
            >
                <Box style={{ flex: 1, position: 'relative' }}>
                    <AnimatePresence mode="wait">
                        {setupStep === 0 && (
                            <SetupStep
                                form={form}
                                setForm={setForm}
                                errors={errors}
                                setErrors={setErrors}
                                handleKeyDown={handleKeyDown}
                                variants={stepVariants}
                            />
                        )}

                        {setupStep === 1 && (
                            <IdentityStep
                                form={form}
                                setForm={setForm}
                                errors={errors}
                                setErrors={setErrors}
                                handleKeyDown={handleKeyDown}
                                generateBotName={generateBotName}
                                variants={stepVariants}
                            />
                        )}

                        {setupStep === 2 && (
                            <LaunchStep
                                loading={loading}
                                form={form}
                                setForm={setForm}
                                errors={errors}
                                setErrors={setErrors}
                                handleKeyDown={handleKeyDown}
                                variants={stepVariants}
                            />
                        )}
                    </AnimatePresence>
                </Box>

                {!loading && (
                    <Group justify={setupStep > 0 ? "space-between" : "flex-end"} mt="auto" w="100%">
                        {setupStep > 0 && <Button variant="subtle" onClick={() => setSetupStep((c) => c - 1)}>Back</Button>}
                        {setupStep < 2 && (
                            <Button 
                                onClick={handleNext} 
                                disabled={setupStep === 0 && !form.pin.trim()}
                            >
                                Next Step
                            </Button>
                        )}
                        {setupStep === 2 &&
                            <Button variant="gradient" rightSection={<IconPlayerPlay size={16} />} onClick={launchBots} gradient={{ from: COLORS.INDIGO, to: COLORS.CYAN }}>Launch Bots</Button>
                        }
                    </Group>
                )}
            </Paper>
        </Box>
    );
});

export default BotSetupWizard;